import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { StatusCode } from "api/protocols";
import { GetProduct } from "api/rpc/2024-04/facilityAdmin/product/product";
import { IProduct, IVariant } from "redux/reducers/models/product";
import { useAppDispatch } from "hooks/redux";
import useDebounce from "hooks/events/useDebounce";
import { showError } from "redux/actions/ui";
import SelectNew, { TSelectOption } from "components/select/SelectNew";

import { PRICING_SHEET_PRODUCT_TYPES } from "../pricingSheetApplications";

const QUICK_ADD_INPUT_ID = "pricing_sheet_product_quick_add";
const PRODUCT_SEARCH_LIMIT = 50;
const PRODUCT_SEARCH_DEBOUNCE_MS = 300;

type TPricingSheetProductSelection = {
  product: IProduct;
  variant?: IVariant;
  isVariant?: boolean;
  isProductHeader?: boolean;
  displayLabel?: string;
};

type TPricingSheetProductQuickAddProps = {
  onAddVariant: (product: IProduct, variant: IVariant) => void;
  addedVariantIds?: number[];
  helpText?: string | null;
  placeholder?: string;
};

function getProductDisplayTitle(product: IProduct) {
  return product.preferred_title || product.title || "";
}

function isSelectableVariantOption(option: TSelectOption<TPricingSheetProductSelection>) {
  return Boolean(option.extraValue?.variant) && !option.extraValue?.isProductHeader;
}

function isAddedVariantOption(option: TSelectOption<TPricingSheetProductSelection>) {
  return isSelectableVariantOption(option) && option.disabled;
}

function getQuickAddOptionLabel(option: TSelectOption<TPricingSheetProductSelection>) {
  if (option.extraValue?.isProductHeader) {
    return option.label;
  }

  return option.extraValue?.displayLabel ?? option.label;
}

function renderQuickAddOption(option: TSelectOption<TPricingSheetProductSelection>) {
  return (
    <>
      <p
        className={classNames("ui-select_dropdown_text", {
          "pricing-sheets-quick-add-option-product": option.extraValue?.isProductHeader,
          "pricing-sheets-quick-add-option-variant": option.extraValue?.isVariant,
          "pricing-sheets-quick-add-option-added": isAddedVariantOption(option),
        })}
      >
        {getQuickAddOptionLabel(option)}
      </p>
      {isAddedVariantOption(option) ? (
        <FontAwesomeIcon className="ui-select_dropdown_check" icon={["far", "check"]} />
      ) : null}
    </>
  );
}

function buildProductVariantOptions(
  products: IProduct[],
  addedVariantIds: Set<number>,
): TSelectOption<TPricingSheetProductSelection>[] {
  const options: TSelectOption<TPricingSheetProductSelection>[] = [];

  products.forEach(product => {
    const title = getProductDisplayTitle(product);
    const variants = product.variants ?? [];

    if (variants.length === 0) {
      return;
    }

    if (variants.length === 1) {
      const variant = variants[0];

      options.push({
        id: variant.id,
        label: title,
        disabled: addedVariantIds.has(variant.id),
        extraValue: { product, variant },
      });
      return;
    }

    options.push({
      id: `product-${product.id}`,
      label: title,
      disabled: true,
      extraValue: { product, isProductHeader: true },
    });

    variants.forEach(variant => {
      const displayLabel = variant.title || title;

      options.push({
        id: variant.id,
        label: `${title} ${displayLabel}`,
        disabled: addedVariantIds.has(variant.id),
        extraValue: {
          product,
          variant,
          isVariant: true,
          displayLabel,
        },
      });
    });
  });

  return options;
}

export default function PricingSheetProductQuickAdd({
  onAddVariant,
  addedVariantIds = [],
  helpText,
  placeholder,
}: TPricingSheetProductQuickAddProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, PRODUCT_SEARCH_DEBOUNCE_MS);
  const [products, setProducts] = useState<IProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const source = axios.CancelToken.source();

    async function loadProducts(token: CancelToken) {
      setIsLoading(true);

      try {
        const res = await GetProduct(
          {
            search: debouncedSearch.trim() || undefined,
            variants: true,
            types: [...PRICING_SHEET_PRODUCT_TYPES],
            limit: PRODUCT_SEARCH_LIMIT,
          },
          false,
          token,
        );

        if (token.reason) {
          return;
        }

        if (res.status !== StatusCode.OK) {
          dispatch(showError(t("Error loading products")));
          setProducts([]);
          return;
        }

        setProducts(res.data ?? []);
      } finally {
        if (!token.reason) {
          setIsLoading(false);
        }
      }
    }

    void loadProducts(source.token);

    return () => {
      source.cancel();
    };
  }, [dispatch, debouncedSearch, t]);

  const addedVariantIdSet = useMemo(() => new Set(addedVariantIds), [addedVariantIds]);
  const productOptions = useMemo(
    () => buildProductVariantOptions(products, addedVariantIdSet),
    [products, addedVariantIdSet],
  );

  function handleSelect(_value: string | number, extraValue?: TPricingSheetProductSelection) {
    const variant = extraValue?.variant;
    if (!extraValue?.product || !variant || addedVariantIdSet.has(variant.id)) {
      return;
    }

    onAddVariant(extraValue.product, variant);
    setSearch("");
  }

  return (
    <div className="pricing-sheets-quick-add">
      <div className="pricing-sheets-quick-add-input">
        <SelectNew<string | number, TPricingSheetProductSelection>
          id={QUICK_ADD_INPUT_ID}
          className="pricing-sheets-quick-add-select"
          labelHidden
          searchable={PRODUCT_SEARCH_DEBOUNCE_MS}
          placeholder={placeholder ?? t("Quick add – type a product name...")}
          value={null as unknown as string | number}
          options={productOptions}
          isLoading={isLoading}
          onSearchCallback={setSearch}
          onChange={handleSelect}
          renderOption={renderQuickAddOption}
        />
      </div>
      {helpText !== null ? (
        <p className="pricing-sheets-quick-add-help">
          {helpText ?? t("Search to add one, or browse to pick several.")}
        </p>
      ) : null}
    </div>
  );
}
